import { Badge } from '@svs/ui/react/badge';
import type React from 'react';
import type { ReactNode } from 'react';

type CouponEventInitProps = {
  result: [number, number] | undefined;
  isCancelled?: boolean;
  isCorrectOutcome?: boolean;
  variantOverride?: React.ComponentProps<typeof Badge>['variant'];
};

/**
 * Component for rendering the result of a coupon event.
 *
 * @param root0 - The props for the CouponEventInit component.
 * @param root0.result - The result of the coupon event, represented as a tuple of two numbers.
 * @param root0.isCancelled - Indicates if the event is cancelled.
 * @param root0.isCorrectOutcome - Indicates if the outcome of the event is correct.
 * @param root0.variantOverride - Optional variant override for styling the badge.
 * @returns A ReactNode that renders the result of the coupon event.
 */
export function CouponInitResult({
  result,
  isCancelled,
  isCorrectOutcome,
  variantOverride,
}: CouponEventInitProps): ReactNode | undefined {
  if (!result) {
    return undefined;
  }
  const variant = isCorrectOutcome ? 'success' : 'important';
  if (isCancelled) {
    return (
      <Badge className="align-center flex justify-center text-xs lg:text-sm" emphasis="low" variant="neutral">
        -
      </Badge>
    );
  }
  const [homeScore, awayScore] = result;

  return (
    <Badge
      className="align-center flex justify-center text-xs lg:text-sm"
      emphasis="low"
      variant={variantOverride ?? variant}
    >
      {homeScore} - {awayScore}
    </Badge>
  );
}
